import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle, XCircle, ArrowRight, MailOpen } from 'lucide-react';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('loading'); // loading | success | error
  const [message, setMessage] = useState('');
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const hasCalled = React.useRef(false);

  useEffect(() => {
    document.title = 'Verify Email | CoinPayUp';
    if (!token) {
      setStatus('error');
      setMessage('No verification token found in this link.');
      return;
    }
    // Guard against React 18 StrictMode double-invocation (prevents token being consumed twice)
    if (hasCalled.current) return;
    hasCalled.current = true;
    const verify = async () => {
      try {
        const { data } = await axios.get(`${API}/auth/verify-email`, { params: { token } });
        setStatus('success');
        setMessage(data.message);
        setVerifiedEmail(data.email || '');
      } catch (e) {
        setStatus('error');
        setMessage(e.response?.data?.detail || 'Verification failed. The link may be invalid or expired.');
      }
    };
    verify();
  }, [token]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="verify-email-page">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent" />

      <div className="w-full max-w-md relative z-10 text-center">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-10">
          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
            <span className="text-white font-bold text-xl">C</span>
          </div>
          <span className="font-bold text-2xl">CoinPayUp</span>
        </div>

        <div className="bg-card border border-border rounded-2xl p-8">
          {status === 'loading' && (
            <div data-testid="verify-loading">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
                <Loader2 className="h-8 w-8 text-primary animate-spin" />
              </div>
              <h2 className="text-xl font-semibold text-foreground mb-2">Verifying your email…</h2>
              <p className="text-sm text-muted-foreground">Please wait a moment.</p>
            </div>
          )}

          {status === 'success' && (
            <div data-testid="verify-success">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-5">
                <CheckCircle className="h-8 w-8 text-emerald-400" />
              </div>
              <h2 className="text-xl font-semibold text-foreground mb-2">Email Verified!</h2>
              <p className="text-sm text-muted-foreground mb-1">
                {verifiedEmail && <span className="font-medium text-foreground">{verifiedEmail}</span>}
              </p>
              <p className="text-sm text-muted-foreground mb-6">Your account is now active. You can log in.</p>
              <Button asChild className="w-full bg-primary text-white" data-testid="verify-login-btn">
                <Link to="/login">
                  Go to Login <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div data-testid="verify-error">
              <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-5">
                <XCircle className="h-8 w-8 text-red-400" />
              </div>
              <h2 className="text-xl font-semibold text-foreground mb-2">Verification Failed</h2>
              <p className="text-sm text-muted-foreground mb-6">{message}</p>
              <div className="space-y-3">
                <Button asChild variant="outline" className="w-full" data-testid="verify-resend-btn">
                  <Link to="/login?resend=1">
                    <MailOpen className="h-4 w-4 mr-2" /> Resend Verification Email
                  </Link>
                </Button>
                <Button asChild variant="ghost" className="w-full text-muted-foreground" data-testid="verify-back-btn">
                  <Link to="/login">Back to Login</Link>
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
